import type {
  AbortionManagementCode,
  BloodGroupCode,
  CoreData,
  CurrentPregnancyData,
  DeliveryPlaceCode,
  DeliveryTypeCode,
  DisabilityCode,
  DisabilityState,
  ExtractionModalityCode,
  FamilyConditionCode,
  FamilyConditionState,
  FamilyMemberCode,
  HistoryListState,
  HistorySection,
  IufdExpulsionCode,
  MaternalComplicationCode,
  MedicalCoverageCode,
  NeonatalComplicationCode,
  NewbornSexCode,
  NewbornVitalStatusCode,
  ObstetricAnomalyType,
  OtherFamilyEntry,
  OtherFamilyState,
  PreventiveCareData,
  PregnancyTermStatusCode,
  PregnancyTypeCode,
  PriorDeliveryRowState,
  RhesusFactorCode,
  WomanOriginCode,
} from './types';

export const INITIAL_CORE: CoreData = {
  patient_last_name: '',
  patient_name: '',
  patient_middle_name: '',
  birth_date: '',
  age_years: null,
  cin: '',
  address: '',
  education_level: '',
  occupation: '',
  coverage_mode: '',
  medical_coverage_label: '',
  woman_origin: '',
  marital_status: '',
  consanguinity: null,
  spouse_name: '',
  spouse_occupation: '',
  phone_number: '',
  email: '',
  reminders_opt_out: false,
  habit_caffeine: null,
  habit_alcohol: null,
  habit_tobacco: null,
  habit_medications: null,
  habit_geophagy: null,
  habit_other_notes: '',
  blood_group_abo: '',
  blood_group_rhesus: '',
  husband_blood_group_abo: '',
  husband_blood_group_rhesus: '',
  weight_kg: null,
  height_cm: null,
};

export const BLOOD_GROUP_OPTIONS: {
  value: BloodGroupCode;
  labelKey: string;
}[] = [
  { value: 'A', labelKey: 'dossier_blood_group_A' },
  { value: 'B', labelKey: 'dossier_blood_group_B' },
  { value: 'O', labelKey: 'dossier_blood_group_O' },
  { value: 'AB', labelKey: 'dossier_blood_group_AB' },
];

export const RHESUS_OPTIONS: {
  value: RhesusFactorCode;
  labelKey: string;
}[] = [
  { value: 'POSITIVE', labelKey: 'dossier_blood_group_rhesus_positive' },
  { value: 'NEGATIVE', labelKey: 'dossier_blood_group_rhesus_negative' },
];

export const WOMAN_ORIGIN_OPTIONS: {
  value: WomanOriginCode;
  labelKey: string;
}[] = [
  { value: 'RURAL', labelKey: 'dossier_origin_rural' },
  { value: 'URBAN', labelKey: 'dossier_origin_urban' },
  { value: 'OTHER', labelKey: 'dossier_origin_other' },
];

// Moroccan medical-coverage schemes. The code is what we persist in
// `patients.medical_coverage_label` (free-text column on the backend) so the
// dropdown both drives the UI and keeps the stored value normalised.
export const MEDICAL_COVERAGE_OPTIONS: {
  value: MedicalCoverageCode;
  labelKey: string;
}[] = [
  { value: 'CNSS', labelKey: 'dossier_medical_coverage_cnss' },
  { value: 'CNOPS', labelKey: 'dossier_medical_coverage_cnops' },
  { value: 'FAR', labelKey: 'dossier_medical_coverage_far' },
  { value: 'RAMED', labelKey: 'dossier_medical_coverage_ramed' },
  { value: 'MUTUELLE', labelKey: 'dossier_medical_coverage_mutuelle' },
  { value: 'NON_INSURED', labelKey: 'dossier_medical_coverage_non_insured' },
];

export type PredefinedFamilyConditionCode = Exclude<
  FamilyConditionCode,
  'OTHER'
>;

export const FAMILY_CONDITIONS: {
  code: PredefinedFamilyConditionCode;
  labelKey: string;
  icon: string;
}[] = [
  { code: 'HTA', labelKey: 'dossier_family_hta', icon: '🩺' },
  { code: 'DIABETES', labelKey: 'dossier_family_diabetes', icon: '🩸' },
  {
    code: 'HEREDITARY_DISEASE',
    labelKey: 'dossier_family_hereditary',
    icon: '🧬',
  },
  { code: 'MALFORMATION', labelKey: 'dossier_family_malformation', icon: '🫀' },
  { code: 'ALLERGY', labelKey: 'dossier_family_allergy', icon: '⚠️' },
];

export const EMPTY_FAMILY: Record<
  PredefinedFamilyConditionCode,
  FamilyConditionState
> = {
  HTA: { active: false, wife_members: [], husband_members: [] },
  DIABETES: { active: false, wife_members: [], husband_members: [] },
  HEREDITARY_DISEASE: { active: false, wife_members: [], husband_members: [] },
  MALFORMATION: { active: false, wife_members: [], husband_members: [] },
  ALLERGY: { active: false, wife_members: [], husband_members: [] },
};

export const FAMILY_MEMBER_OPTIONS: {
  value: FamilyMemberCode;
  labelKey: string;
}[] = [
  { value: 'MOTHER', labelKey: 'dossier_family_member_mother' },
  { value: 'FATHER', labelKey: 'dossier_family_member_father' },
  { value: 'GRANDMOTHER', labelKey: 'dossier_family_member_grandmother' },
  { value: 'GRANDFATHER', labelKey: 'dossier_family_member_grandfather' },
  { value: 'SISTER', labelKey: 'dossier_family_member_sister' },
  { value: 'BROTHER', labelKey: 'dossier_family_member_brother' },
  { value: 'AUNT', labelKey: 'dossier_family_member_aunt' },
  { value: 'UNCLE', labelKey: 'dossier_family_member_uncle' },
  { value: 'COUSIN', labelKey: 'dossier_family_member_cousin' },
  { value: 'CHILD', labelKey: 'dossier_family_member_child' },
  { value: 'OTHER', labelKey: 'dossier_family_member_other' },
];

let __otherEntrySeq = 0;
export const newOtherFamilyEntry = (): OtherFamilyEntry => ({
  id: `other_${Date.now().toString(36)}_${(__otherEntrySeq++).toString(36)}`,
  label: '',
  wife_members: [],
  husband_members: [],
});

export const EMPTY_OTHER_FAMILY: OtherFamilyState = {
  active: false,
  entries: [],
};

export const emptyHistoryList = (): HistoryListState => ({
  active: false,
  items: [''],
});

export const INITIAL_HISTORY_LISTS: Record<HistorySection, HistoryListState> = {
  medical: emptyHistoryList(),
  surgical: emptyHistoryList(),
  gynecological: emptyHistoryList(),
};

export const HISTORY_SECTION_META: {
  code: HistorySection;
  icon: string;
  titleKey: string;
  helperKey: string;
  placeholderKey: string;
}[] = [
  {
    code: 'medical',
    icon: '🩺',
    titleKey: 'dossier_medical_section_title',
    helperKey: 'dossier_medical_section_helper',
    placeholderKey: 'dossier_medical_entry_placeholder',
  },
  {
    code: 'surgical',
    icon: '🔪',
    titleKey: 'dossier_surgical_section_title',
    helperKey: 'dossier_surgical_section_helper',
    placeholderKey: 'dossier_surgical_entry_placeholder',
  },
  {
    code: 'gynecological',
    icon: '🌸',
    titleKey: 'dossier_gynecological_section_title',
    helperKey: 'dossier_gynecological_section_helper',
    placeholderKey: 'dossier_gynecological_entry_placeholder',
  },
];

export const INITIAL_DISABILITY: DisabilityState = {
  types: [],
  other_description: '',
};

export const INITIAL_PREVENTIVE_CARE: PreventiveCareData = {
  vat_dose: null,
  rubella_vaccinated: null,
  rubella_vaccination_date: '',
  hepatitis_b_vaccinated: null,
  hepatitis_b_vaccination_date: '',
  cervical_smear_iva_date: '',
};

export const DISABILITY_OPTIONS: {
  value: DisabilityCode;
  labelKey: string;
}[] = [
  { value: 'MOTOR', labelKey: 'dossier_disability_motor' },
  { value: 'SENSORY', labelKey: 'dossier_disability_sensory' },
  { value: 'PSYCHIC', labelKey: 'dossier_disability_psychic' },
  { value: 'MENTAL', labelKey: 'dossier_disability_mental' },
  { value: 'OTHER', labelKey: 'dossier_disability_other' },
];

export const OBSTETRIC_ANOMALY_TYPES: {
  value: ObstetricAnomalyType;
  labelKey: string;
}[] = [
  { value: 'AVORTEMENT', labelKey: 'dossier_abortion' },
  { value: 'IUFD', labelKey: 'dossier_iufd' },
  { value: 'CONGENITAL', labelKey: 'dossier_congenital' },
  { value: 'OTHER', labelKey: 'dossier_others' },
];

export const DELIVERY_TYPE_OPTIONS: {
  value: DeliveryTypeCode;
  labelKey: string;
}[] = [
  { value: 'NORMAL', labelKey: 'dossier_delivery_type_normal' },
  { value: 'CESAREAN', labelKey: 'dossier_delivery_type_cesarean' },
  { value: 'INSTRUMENTAL', labelKey: 'dossier_delivery_type_instrumental' },
  { value: 'OTHER', labelKey: 'dossier_delivery_type_other' },
];

export const EXTRACTION_MODALITY_OPTIONS: {
  value: ExtractionModalityCode;
  labelKey: string;
}[] = [
  { value: 'SPONTANEOUS', labelKey: 'dossier_extraction_spontaneous' },
  { value: 'VACUUM', labelKey: 'dossier_extraction_vacuum' },
  { value: 'FORCEPS', labelKey: 'dossier_extraction_forceps' },
  { value: 'CESAREAN', labelKey: 'dossier_extraction_cesarean' },
  { value: 'BREECH', labelKey: 'dossier_extraction_breech' },
  { value: 'OTHER', labelKey: 'dossier_extraction_other' },
];

export const extractionOptionsForDeliveryType = (
  deliveryType: DeliveryTypeCode | ''
): { value: ExtractionModalityCode; labelKey: string }[] => {
  switch (deliveryType) {
    case 'NORMAL':
      return EXTRACTION_MODALITY_OPTIONS.filter((o) =>
        ['SPONTANEOUS', 'BREECH', 'OTHER'].includes(o.value)
      );
    case 'INSTRUMENTAL':
      return EXTRACTION_MODALITY_OPTIONS.filter((o) =>
        ['VACUUM', 'FORCEPS', 'OTHER'].includes(o.value)
      );
    default:
      return [];
  }
};

export const MATERNAL_COMPLICATION_OPTIONS: {
  value: MaternalComplicationCode;
  labelKey: string;
}[] = [
  { value: 'NONE', labelKey: 'dossier_maternal_comp_none' },
  { value: 'HEMORRHAGE', labelKey: 'dossier_maternal_comp_hemorrhage' },
  { value: 'PREECLAMPSIA', labelKey: 'dossier_maternal_comp_preeclampsia' },
  { value: 'ECLAMPSIA', labelKey: 'dossier_maternal_comp_eclampsia' },
  { value: 'INFECTION', labelKey: 'dossier_maternal_comp_infection' },
  { value: 'TEAR', labelKey: 'dossier_maternal_comp_tear' },
  { value: 'OTHER', labelKey: 'dossier_maternal_comp_other' },
];

export const NEONATAL_COMPLICATION_OPTIONS: {
  value: NeonatalComplicationCode;
  labelKey: string;
}[] = [
  { value: 'NONE', labelKey: 'dossier_neonatal_comp_none' },
  {
    value: 'RESPIRATORY_DISTRESS',
    labelKey: 'dossier_neonatal_comp_respiratory',
  },
  { value: 'ASPHYXIA', labelKey: 'dossier_neonatal_comp_asphyxia' },
  { value: 'LOW_BIRTH_WEIGHT', labelKey: 'dossier_neonatal_comp_low_weight' },
  { value: 'INFECTION', labelKey: 'dossier_neonatal_comp_infection' },
  { value: 'MALFORMATION', labelKey: 'dossier_neonatal_comp_malformation' },
  { value: 'OTHER', labelKey: 'dossier_neonatal_comp_other' },
];

export const DELIVERY_PLACE_OPTIONS: {
  value: DeliveryPlaceCode;
  labelKey: string;
}[] = [
  { value: 'HOSPITAL', labelKey: 'dossier_delivery_place_hospital' },
  { value: 'MATERNITY', labelKey: 'dossier_delivery_place_maternity' },
  { value: 'CLINIC', labelKey: 'dossier_delivery_place_clinic' },
  { value: 'HOME', labelKey: 'dossier_delivery_place_home' },
  { value: 'OTHER', labelKey: 'dossier_delivery_place_other' },
];

export const NEWBORN_SEX_OPTIONS: {
  value: NewbornSexCode;
  labelKey: string;
}[] = [
  { value: 'M', labelKey: 'dossier_newborn_sex_male' },
  { value: 'F', labelKey: 'dossier_newborn_sex_female' },
  { value: 'UNKNOWN', labelKey: 'dossier_newborn_sex_unknown' },
];

export const NEWBORN_VITAL_STATUS_OPTIONS: {
  value: NewbornVitalStatusCode;
  labelKey: string;
}[] = [
  { value: 'ALIVE', labelKey: 'dossier_newborn_vital_alive' },
  { value: 'DECEASED_NEONATAL', labelKey: 'dossier_newborn_vital_deceased' },
  { value: 'STILLBIRTH', labelKey: 'dossier_newborn_vital_stillbirth' },
];

export const ABORTION_MANAGEMENT_OPTIONS: {
  value: AbortionManagementCode;
  labelKey: string;
}[] = [
  { value: 'SPONTANEOUS', labelKey: 'dossier_abortion_mgmt_spontaneous' },
  { value: 'CURETAGE', labelKey: 'dossier_abortion_mgmt_curetage' },
  { value: 'ASPIRATION', labelKey: 'dossier_abortion_mgmt_aspiration' },
  { value: 'MEDICAL', labelKey: 'dossier_abortion_mgmt_medical' },
  { value: 'OTHER', labelKey: 'dossier_abortion_mgmt_other' },
];

export const IUFD_EXPULSION_OPTIONS: {
  value: IufdExpulsionCode;
  labelKey: string;
}[] = [
  { value: 'SPONTANEOUS', labelKey: 'dossier_iufd_expulsion_spontaneous' },
  { value: 'INDUCED', labelKey: 'dossier_iufd_expulsion_induced' },
  { value: 'CESAREAN', labelKey: 'dossier_iufd_expulsion_cesarean' },
  { value: 'OTHER', labelKey: 'dossier_iufd_expulsion_other' },
];

export const PREGNANCY_TERM_STATUS_OPTIONS: {
  value: PregnancyTermStatusCode;
  labelKey: string;
}[] = [
  { value: 'PRETERM', labelKey: 'dossier_term_status_preterm' },
  { value: 'TERM', labelKey: 'dossier_term_status_term' },
  { value: 'POSTTERM', labelKey: 'dossier_term_status_postterm' },
];

export const derivePregnancyTermStatus = (
  weeks: number | null,
  days: number | null
): PregnancyTermStatusCode | '' => {
  if (weeks === null || Number.isNaN(weeks)) return '';
  const safeDays = days ?? 0;
  const totalDays = weeks * 7 + safeDays;
  if (totalDays < 37 * 7) return 'PRETERM';
  if (totalDays >= 42 * 7) return 'POSTTERM';
  return 'TERM';
};

export const emptyPriorDelivery = (
  withAnomaly = false
): PriorDeliveryRowState => ({
  delivery_date: '',
  delivery_type: '',
  delivery_ga_weeks: null,
  delivery_ga_days: null,
  pregnancy_term_status: '',
  extraction_modalities: '',
  cesarean_indication: '',
  delivery_place: '',
  delivery_complication_type: '',
  newborn_complication_type: '',
  newborn_weight_grams: null,
  newborn_sex: '',
  newborn_vital_status: '',
  has_obstetric_anomalies: withAnomaly,
  anomaly_type: '',
  anomaly_date: '',
  anomaly_place: '',
  anomaly_ga_weeks: null,
  anomaly_ga_days: null,
  anomaly_description: '',
});

export const PREGNANCY_TYPE_OPTIONS: {
  value: PregnancyTypeCode;
  labelKey: string;
}[] = [
  { value: 'UNIQUE', labelKey: 'dossier_pregnancy_type_unique' },
  { value: 'TWIN', labelKey: 'dossier_pregnancy_type_twin' },
  { value: 'TRIPLET', labelKey: 'dossier_pregnancy_type_triplet' },
  { value: 'MULTIPLE', labelKey: 'dossier_pregnancy_type_multiple' },
  { value: 'OTHER', labelKey: 'dossier_pregnancy_type_other' },
];

export const PREGNANCY_RISK_OPTIONS: {
  value:
    | 'risque_anemie'
    | 'risque_hta'
    | 'risque_diabete'
    | 'risque_cardiopathie'
    | 'risque_metrorragie'
    | 'risque_infection'
    | 'risque_preeclampsie'
    | 'risque_eclampsie';
  labelKey: string;
}[] = [
  { value: 'risque_anemie', labelKey: 'dossier_pregnancy_risk_anemia' },
  { value: 'risque_hta', labelKey: 'dossier_pregnancy_risk_hypertension' },
  { value: 'risque_diabete', labelKey: 'dossier_pregnancy_risk_diabetes' },
  {
    value: 'risque_cardiopathie',
    labelKey: 'dossier_pregnancy_risk_cardiopathy',
  },
  {
    value: 'risque_metrorragie',
    labelKey: 'dossier_pregnancy_risk_metrorrhagia',
  },
  { value: 'risque_infection', labelKey: 'dossier_pregnancy_risk_infection' },
  {
    value: 'risque_preeclampsie',
    labelKey: 'dossier_pregnancy_risk_preeclampsia',
  },
  { value: 'risque_eclampsie', labelKey: 'dossier_pregnancy_risk_eclampsia' },
];

export const INITIAL_PREGNANCY: CurrentPregnancyData = {
  grossesse_type: '',
  grossesse_classee_a_risque: null,
  risque_anemie: false,
  risque_hta: false,
  risque_diabete: false,
  risque_cardiopathie: false,
  risque_metrorragie: false,
  risque_infection: false,
  risque_preeclampsie: false,
  risque_eclampsie: false,
  risque_autre_details: '',
  last_menstrual_period: '',
  estimated_due_date: '',
  lmp_is_estimated: false,
  edd_is_estimated: false,
  ga_weeks: '',
  ga_days: '',
  grossesse_desiree: null,
  preg_crl: null,
  preg_bpd: null,
  preg_hc: null,
  preg_ac: null,
  preg_fl: null,
};

export const MAX_CURRENT_GA_WEEKS = 45;
export const MAX_CURRENT_GA_DAYS = 6;
export const MAX_CURRENT_GA_TOTAL_DAYS =
  MAX_CURRENT_GA_WEEKS * 7 + MAX_CURRENT_GA_DAYS;

export const selectCls =
  'flex h-9 w-full rounded-lg border border-slate-300 dark:border-gray-600 bg-transparent dark:bg-gray-800 px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 text-title-page-color-300 dark:text-gray-300 transition-colors';
